/**
 * UX Activity Tracker — Раздел 3 глобального плана
 * 
 * Легковесный модуль отслеживания активности пользователей.
 * Собирает: session_start/end, page_view, action, error.
 * Отправляет пакетно каждые 30 сек или при смене страницы.
 * Использует navigator.sendBeacon при закрытии вкладки.
 */

const API_URL = import.meta.env.VITE_API_URL || '/api';
const FLUSH_INTERVAL = 30_000; // 30 сек
const MAX_BATCH_SIZE = 50;

let buffer = [];
let sessionId = null;
let currentPage = null;
let pageEnteredAt = null;
let flushTimer = null;
let isInitialized = false;

// ─── Генерация уникального ID сессии ──────────────────────────
function generateSessionId() {
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

// ─── Получение токена ─────────────────────────────────────────
function getToken() {
  return localStorage.getItem('token');
}

// ─── Инициализация трекера ────────────────────────────────────
export function initTracker() {
  if (isInitialized) return;
  isInitialized = true;

  sessionId = generateSessionId();

  // session_start
  pushEvent({
    event_type: 'session_start',
    details: {
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      user_agent: navigator.userAgent,
      language: navigator.language,
      referrer: document.referrer || null,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  });

  // Автосброс буфера каждые 30 сек
  flushTimer = setInterval(() => flushEvents(), FLUSH_INTERVAL);

  // При закрытии вкладки — отправляем оставшееся
  window.addEventListener('beforeunload', handleBeforeUnload);
  document.addEventListener('visibilitychange', handleVisibilityChange);
}

// ─── Остановка трекера ────────────────────────────────────────
export function stopTracker() {
  if (!isInitialized) return;

  // session_end с продолжительностью
  if (currentPage && pageEnteredAt) {
    pushEvent({
      event_type: 'page_view',
      page_name: currentPage,
      details: { duration_ms: Date.now() - pageEnteredAt },
    });
  }

  pushEvent({
    event_type: 'session_end',
    details: {},
  });

  flushEvents(true); // force beacon
  
  clearInterval(flushTimer);
  window.removeEventListener('beforeunload', handleBeforeUnload);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  
  isInitialized = false;
  sessionId = null;
  currentPage = null;
  pageEnteredAt = null;
  buffer = [];
}

// ─── Обработчики ──────────────────────────────────────────────
function handleBeforeUnload() {
  // Фиксируем последнюю страницу
  if (currentPage && pageEnteredAt) {
    pushEvent({
      event_type: 'page_view',
      page_name: currentPage,
      details: { duration_ms: Date.now() - pageEnteredAt },
    });
  }
  pushEvent({ event_type: 'session_end', details: {} });
  flushEvents(true); // sendBeacon
}

function handleVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    flushEvents(true);
  }
}

// ─── Добавление события в буфер ───────────────────────────────
function pushEvent(event) {
  if (!sessionId) return;
  buffer.push({
    ...event,
    session_id: sessionId,
    timestamp: new Date().toISOString(),
  });

  // Если буфер переполнен — сбрасываем
  if (buffer.length >= MAX_BATCH_SIZE) {
    flushEvents();
  }
}

// ─── Отправка буфера на сервер ────────────────────────────────
function flushEvents(useBeacon = false) {
  if (buffer.length === 0) return;

  const events = [...buffer];
  buffer = [];

  const token = getToken();
  if (!token) return; // не отправляем без авторизации

  const body = JSON.stringify({ events });

  if (useBeacon && navigator.sendBeacon) {
    // sendBeacon для гарантированной отправки при закрытии
    const blob = new Blob([body], { type: 'application/json' });
    // sendBeacon не поддерживает кастомные заголовки — используем query param
    navigator.sendBeacon(`${API_URL}/ux-logs?token=${token}`, blob);
  } else {
    // Обычный fetch (не await, fire-and-forget)
    fetch(`${API_URL}/ux-logs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body,
      keepalive: true, // важно для закрытия вкладки
    }).catch(() => {
      // Если ошибка — возвращаем в буфер
      buffer.push(...events);
    });
  }
}

// ─── Публичные методы для использования в компонентах ──────────

/**
 * Трекинг перехода на новую страницу
 */
export function trackPageView(pageName) {
  if (!isInitialized || !pageName) return;

  // Закрываем предыдущую страницу с длительностью
  if (currentPage && pageEnteredAt) {
    pushEvent({
      event_type: 'page_view',
      page_name: currentPage,
      details: { duration_ms: Date.now() - pageEnteredAt },
    });
  }

  currentPage = pageName;
  pageEnteredAt = Date.now();
}

/**
 * Трекинг пользовательского действия (клик по ключевой кнопке)
 */
export function trackAction(actionName, details = {}) {
  if (!isInitialized) return;
  pushEvent({
    event_type: 'action',
    page_name: currentPage,
    target_element: actionName,
    details,
  });
}

/**
 * Трекинг ошибки (API / UI)
 */
export function trackError(errorMessage, details = {}) {
  if (!isInitialized) return;
  pushEvent({
    event_type: 'error',
    page_name: currentPage,
    details: {
      error_message: errorMessage,
      ...details,
    },
  });
}
